import "./AdminPage.scss";
import 'rsuite/useToaster/styles/index.css';
import 'rsuite/Message/styles/index.css';
import {CSSProperties, DragEvent, FormEvent, useCallback, useEffect, useMemo, useRef, useState} from "react";
import axios from "axios";
import {PATHS_API} from "../../../enum";
import {useCategoriesStore} from "../../../modules/shop/categories/store";
import {useServersStore} from "../../../modules/shop/servers/store";
import {useProductsStore} from "../../../modules/shop/products/store";
import ProductImage from "../../shared/ProductImage/ProductImage";
import Toaster, {ToasterProps} from "../../shared/Toaster/Toaster";
import {getCurrentDateInSeconds} from "../../shared/functions/getRandomId";
import ProductEditor from "./ProductEditor";
import MergePanel from "./MergePanel";
import PaymentProblems from "./PaymentProblems";
import SystemAlerts from "./SystemAlerts";
import LootboxServers from "./LootboxServers";
import LootboxStats from "./LootboxStats";
import {IAdminProduct, PRODUCT_TYPES, typeLabel} from "./adminTypes";

type Access = "checking" | "allowed" | "locked" | "denied";

const AdminPage = () => {
    const [access, setAccess] = useState<Access>("checking");
    const [products, setProducts] = useState<IAdminProduct[]>([]);
    const [search, setSearch] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [editing, setEditing] = useState<IAdminProduct | "new" | null>(null);
    const [mergeMode, setMergeMode] = useState(false);
    const [selected, setSelected] = useState<string[]>([]);
    const [merging, setMerging] = useState(false);
    const [toastProps, setToastProps] = useState<ToasterProps | null>(null);
    const [dragId, setDragId] = useState<string | null>(null);
    const [online, setOnline] = useState<{ online: number, loggedIn: number, guests: number } | null>(null);
    const orderBeforeDrag = useRef<string[]>([]);

    const categories = useCategoriesStore((s) => s.categories);
    const fetchCategories = useCategoriesStore((s) => s.fetchCategories);
    const servers = useServersStore((s) => s.servers);
    const fetchServers = useServersStore((s) => s.fetchServers);
    const fetchShopProducts = useProductsStore((s) => s.fetchProducts);

    const toast = (message: string, type: ToasterProps["type"] = "success") =>
        setToastProps({ message, type, id: getCurrentDateInSeconds() + message });

    const [pin, setPin] = useState("");
    const [pinError, setPinError] = useState("");
    const [unlocking, setUnlocking] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await axios.get(PATHS_API.ADMIN_PRODUCTS, { withCredentials: true });
            setProducts(data);
        } catch (err: any) {
            // сесія PIN закінчилась (2 год або перезапуск сервера) — просимо PIN знову
            if (err.response?.data?.message === "PIN_REQUIRED") setAccess("locked");
        }
    }, []);

    // сервер вирішує, чи є доступ і чи введено PIN; сторінка лише показує відповідний стан
    useEffect(() => {
        axios.get(PATHS_API.ADMIN_ME, { withCredentials: true })
            .then(({ data }) => {
                if (data.unlocked) { setAccess("allowed"); load(); } else setAccess("locked");
            })
            .catch(() => setAccess("denied"));
        if (!categories.length) fetchCategories();
        if (!servers.length) fetchServers();
    }, []);

    // скільки людей зараз на сайті; оновлюємо кожні 15 секунд, поки адмінка відкрита
    useEffect(() => {
        if (access !== "allowed") return;
        const fetchOnline = () => axios.get(PATHS_API.ADMIN_ONLINE, { withCredentials: true })
            .then(({ data }) => setOnline(data))
            .catch(() => {});
        fetchOnline();
        const timer = window.setInterval(fetchOnline, 15_000);
        return () => window.clearInterval(timer);
    }, [access]);

    const categoryName = useMemo(() => new Map(categories.map((c) => [c._id, c.name])), [categories]);

    const visible = products.filter((p) =>
        (!typeFilter || p.type === typeFilter) &&
        (!search || `${p.name} ${p.className ?? ""}`.toLowerCase().includes(search.toLowerCase()))
    );

    const afterChange = async (message: string) => {
        setEditing(null);
        setMerging(false);
        setMergeMode(false);
        setSelected([]);
        toast(message);
        await load();
        fetchShopProducts(); // щоб головна одразу показала зміни
    };

    const unlock = async (e: FormEvent) => {
        e.preventDefault();
        if (unlocking) return;
        setUnlocking(true);
        setPinError("");
        try {
            await axios.post(PATHS_API.ADMIN_UNLOCK, { pin }, { withCredentials: true });
            setPin("");
            setAccess("allowed");
            load();
        } catch (err: any) {
            setPinError(err.response?.data?.message || "Не вдалося перевірити PIN");
            setPin("");
        } finally {
            setUnlocking(false);
        }
    };

    const lock = async () => {
        await axios.post(PATHS_API.ADMIN_LOCK, {}, { withCredentials: true }).catch(() => {});
        setProducts([]);
        setAccess("locked");
    };

    const remove = async (p: IAdminProduct) => {
        if (!window.confirm(`Видалити «${p.name}»? Копія лишиться в архіві.`)) return;
        try {
            await axios.delete(`${PATHS_API.ADMIN_PRODUCTS}/${p._id}`, { withCredentials: true });
            await afterChange(`Товар «${p.name}» видалено`);
        } catch (err: any) {
            toast(err.response?.data?.message || "Не вдалося видалити", "error");
        }
    };

    // Перетягувати можна лише повний список: у відфільтрованому незрозуміло, куди стають сховані товари
    const canDrag = !search && !typeFilter && !mergeMode;

    const dragStart = (e: DragEvent, id: string) => {
        orderBeforeDrag.current = products.map((p) => p._id);
        e.dataTransfer.effectAllowed = "move";
        setDragId(id);
    };

    // картка їде на місце тієї, над якою її тримають, тож сітка перебудовується одразу
    const dragOver = (e: DragEvent, overId: string) => {
        if (!dragId) return;
        e.preventDefault();
        if (overId === dragId) return;
        setProducts((list) => {
            const from = list.findIndex((p) => p._id === dragId);
            const to = list.findIndex((p) => p._id === overId);
            const next = [...list];
            next.splice(to, 0, next.splice(from, 1)[0]);
            return next;
        });
    };

    const dragEnd = async () => {
        setDragId(null);
        const ids = products.map((p) => p._id);
        if (ids.join() === orderBeforeDrag.current.join()) return;
        try {
            await axios.put(PATHS_API.ADMIN_ORDER, { ids }, { withCredentials: true });
            toast("Порядок збережено");
            fetchShopProducts();
        } catch (err: any) {
            toast(err.response?.data?.message || "Не вдалося зберегти порядок", "error");
            await load();
        }
    };

    const toggleSelect = (id: string) =>
        setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

    if (access === "checking") {
        return <div className="containerContent adm-page"><div className="adm-message">Перевіряємо доступ...</div></div>;
    }
    if (access === "denied") {
        return <div className="containerContent adm-page"><div className="adm-message">Немає доступу до адмінки.</div></div>;
    }
    if (access === "locked") {
        return (
            <div className="containerContent adm-page">
                <form className="adm-pin" onSubmit={unlock}>
                    <div className="adm-pin-icon">🔒</div>
                    <h3>Введіть PIN адмінки</h3>
                    <p className="adm-muted">PIN діє до закриття браузера, але не довше 2 годин.</p>
                    <input
                        className="adm-pin-input"
                        type="password"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        autoFocus
                        maxLength={12}
                        value={pin}
                        onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                        placeholder="••••"
                        aria-label="PIN"
                    />
                    { pinError && <div className="adm-pin-error">{pinError}</div> }
                    <button type="submit" className="adm-primary-btn" disabled={pin.length < 4 || unlocking}>
                        {unlocking ? "Перевіряємо..." : "Увійти"}
                    </button>
                </form>
            </div>
        );
    }

    return (
        <div className="containerContent adm-page">
            <div className="adm-hero">
                <div>
                    <h3 className="adm-title">Адмінка товарів</h3>
                    <p className="adm-subtitle">Створюйте й редагуйте товари, а однакові товари різного кольору об'єднуйте в один із вибором кольору.</p>
                </div>
                <div className="adm-stats">
                    { online &&
                        <div className="adm-online" title={`Гравців: ${online.loggedIn}, гостей: ${online.guests}`}>
                            <b><i className="adm-online-dot" aria-hidden="true" />{online.online}</b>
                            <span>онлайн</span>
                        </div>
                    }
                    <div><b>{products.length}</b><span>товарів</span></div>
                    <div><b>{products.filter((p) => p.variants?.length).length}</b><span>з кольорами</span></div>
                </div>
            </div>

            <SystemAlerts onError={(message) => toast(message, "error")} />
            <PaymentProblems onError={(message) => toast(message, "error")} />
            <LootboxServers onDone={toast} />
            <LootboxStats onError={(message) => toast(message, "error")} />

            <div className="adm-toolbar">
                <input className="adm-search" placeholder="Пошук за назвою чи класнеймом" value={search} onChange={(e) => setSearch(e.target.value)} />
                <select className="adm-select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                    <option value="">Усі типи</option>
                    { PRODUCT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>) }
                </select>
                <button type="button" className={`adm-ghost-btn ${mergeMode ? "on" : ""}`} onClick={() => { setMergeMode(!mergeMode); setSelected([]); }}>
                    {mergeMode ? "Скасувати вибір" : "Об'єднати кольори"}
                </button>
                <button type="button" className="adm-primary-btn" onClick={() => setEditing("new")}>+ Новий товар</button>
                <button type="button" className="adm-ghost-btn" onClick={lock} title="Вийти з сесії PIN">🔒 Заблокувати</button>
            </div>

            { mergeMode &&
                <div className="adm-merge-bar">
                    <span>Позначте однакові товари різного кольору ({selected.length} вибрано)</span>
                    <button type="button" className="adm-primary-btn" disabled={selected.length < 2} onClick={() => setMerging(true)}>Далі</button>
                </div>
            }

            <p className="adm-hint">
                { canDrag
                    ? "Перетягніть картку, щоб змінити її місце в магазині — порядок зберігається одразу."
                    : "Щоб змінювати порядок, очистьте пошук і фільтр типу." }
            </p>

            <div className="adm-grid">
                { visible.map((p, i) =>
                    <div
                        key={p._id}
                        className={`adm-card ${selected.includes(p._id) ? "selected" : ""} ${canDrag ? "draggable" : ""} ${dragId === p._id ? "dragging" : ""}`}
                        style={{ "--i": Math.min(i, 20) } as CSSProperties}
                        draggable={canDrag}
                        onDragStart={(e) => dragStart(e, p._id)}
                        onDragOver={(e) => dragOver(e, p._id)}
                        onDrop={(e) => e.preventDefault()}
                        onDragEnd={dragEnd}
                        onClick={() => mergeMode ? toggleSelect(p._id) : setEditing(p)}
                        title={mergeMode ? undefined : "Натисніть, щоб змінити"}
                    >
                        { mergeMode && <input className="adm-card-check" type="checkbox" checked={selected.includes(p._id)} onChange={() => toggleSelect(p._id)} onClick={(e) => e.stopPropagation()} /> }
                        { !mergeMode &&
                            <button type="button" className="adm-icon-btn adm-danger adm-card-delete" onClick={(e) => { e.stopPropagation(); remove(p); }} aria-label={`Видалити ${p.name}`}>🗑</button>
                        }
                        <div className="adm-card-tags">
                            <span className="adm-tag adm-tag-type">{typeLabel(p.type)}</span>
                            { p.personal && <span className="adm-tag">особистий</span> }
                        </div>
                        <ProductImage className="adm-card-img" src={p.imageUrl} alt="" draggable={false} />
                        { p.variants?.length ? <span className="adm-dots adm-card-dots">{ p.variants.map((v, j) => <i key={j} style={{ background: v.color || "#888" }} title={v.name} />) }</span> : null }
                        <div className="adm-card-info">
                            <p title={p.name}>{p.name}</p>
                            <span className="adm-card-price">{p.price} ₴</span>
                        </div>
                        <div className="adm-card-meta" title={`${p.className || "—"} · ${(p.categories ?? []).map((c) => categoryName.get(c)).filter(Boolean).join(", ") || "без категорії"}`}>
                            {p.className || "—"}
                        </div>
                    </div>
                )}
                { visible.length === 0 && <div className="adm-message">Нічого не знайдено</div> }
            </div>

            { editing &&
                <ProductEditor
                    product={editing === "new" ? null : editing}
                    categories={categories}
                    servers={servers}
                    onClose={() => setEditing(null)}
                    onSaved={afterChange}
                />
            }

            { merging &&
                <MergePanel
                    selected={products.filter((p) => selected.includes(p._id))}
                    onCancel={() => setMerging(false)}
                    onMerged={afterChange}
                />
            }

            {toastProps && <Toaster message={toastProps.message} type={toastProps.type} id={toastProps.id} />}
        </div>
    );
};

export default AdminPage;
